import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from './Navbar';
import api from '../services/api';
import CustomerFormModal from './CustomerFormModal';
import { Customer } from '../types/customer';
import { exportToExcel } from '../utils/excelExport';
import ImagePreview from './ImagePreview';
import { useAuth } from '../contexts/AuthContext';

const pageSizeDefault = 10;

const CustomerDetailsPage: React.FC = () => {
  const [items, setItems] = useState<Customer[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showModal, setShowModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/customers', { 
        params: { page, pageSize: pageSizeDefault, search: search || undefined } 
      });
      const data = res.data?.data ?? res.data;
      setItems(data.items || []);
      setTotalPages(data.totalPages || 1);
    } catch (e) {
      console.error('Failed to load customers', e);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { 
    fetchData();
  }, [fetchData]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
    // fetchData();
  };

  const { user } = useAuth();

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
  };

  const handleAdd = () => {
    setSelectedCustomer(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCustomer(null);
    setShowModal(false);
  };

  const handleSaveCustomer = async (formData: FormData) => {
    try {
      if (selectedCustomer) {
        await api.put(`/customers/${selectedCustomer.id}`, formData);
      } else {
        await api.post('/customers', formData);
      }
      
      handleCloseModal();
      fetchData(); // Refresh list
    } catch (err: any) {
      console.error('Failed to save customer:', err);
      alert(err.response?.data?.message || 'Failed to save customer. Please try again.');
      throw err;
    }
  };

  const handleDelete = async (customerId: number) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      await api.delete(`/customers/${customerId}`);
      fetchData(); // Refresh list after deletion
    } catch (err: any) {
      console.error('Failed to delete customer:', err);
      alert(err.response?.data?.message || 'Failed to delete customer. Please try again.');
    }
  };

  const handleDownload = async () => {
    try {
      // Fetch all data for current search
      const res = await api.get('/customers', { 
        params: { 
          page: 1,
          pageSize: 1000, // Large number to get all records
          search: search || undefined 
        } 
      });
      
      const data = res.data?.data ?? res.data;
      if (data?.items?.length) {
        exportToExcel(data.items, search);
      } else {
        alert('No data available to export');
      }
    } catch (error) {
      console.error('Failed to download data:', error);
      alert('Failed to download data. Please try again.');
    }
  };

  const canPrev = useMemo(() => page > 1, [page]);
  const canNext = useMemo(() => page < totalPages, [page, totalPages]);

  return (
    <div className="min-h-screen bg-secondary-50">
      <Navbar />

      {/* removed side padding to let table stretch to edges */}
      <div className="w-full mx-auto px-0 py-8">
        <div className="flex items-center justify-between mb-6 px-4">
          <h1 className="text-3xl font-serif text-gray-900">Customer Details</h1>
        </div>

        {/* Search and Actions - search input reduced so buttons fit */}
        <div className="mb-6 px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Show Filters commented out */}
              {/* <button className="btn-primary">Show Filters</button> */}

              <form onSubmit={onSearch} className="relative">
                <input
                  className="w-[240px] md:w-[340px] lg:w-[420px] rounded-full border-2 border-[#2f6b54] px-4 py-2 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#2f6b54]"
                  placeholder="Search Customer (Firm Name or Owner Name)"
                  value={searchInput}
                  onChange={(e)=>setSearchInput(e.target.value)}
                />
                <button
                  type="submit"
                  className="absolute right-0 top-1/2 -translate-y-1/2 bg-[#2f6b54] text-white rounded-full w-10 h-10 flex items-center justify-center"
                  aria-label="Search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                </button>
              </form>
            </div>

            <div className="flex items-center gap-3">
              <button 
                className="btn-primary"
                onClick={handleDownload}
              >
                Download List
              </button>
              <button 
                className="btn-primary"
                onClick={handleAdd}
              >
                Add Customer
              </button>
            </div>
          </div>
        </div>

        {/* Table - styled like Employee screen */}
        <div className="bg-white rounded-lg shadow-sm overflow-x-auto border border-[#2f6b54] mx-4">
          <table className="table-auto border-collapse min-w-full minWidthtable">
            <thead>
              <tr className="bg-[#f1f6f1]">
                {user?.isAdmin && <th className="px-2 py-1 border border-[#2f6b54] text-center action-col">Actions</th>}
                <th className="px-2 py-1 border border-[#2f6b54] text-center Srn">Sr.No.</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[220px]">Firm Name</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[200px]">Owner Name</th>

                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Mobile No. 1</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Mobile No. 2</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Mobile No. 3</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Mobile No. 4</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Mobile No. 5</th>

                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Aadhar Card No.</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[300px]">Address</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[160px]">City</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[160px]">State</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Pan Card No.</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Pan Card Photo</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">GST No.</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[200px]">Udhyog Adhar No.</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Udhyog Adhar Category</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">GST Photo</th>
                <th className="px-4 py-1 border border-[#2f6b54] w-[180px]">Udhyog Adhar Photo</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-6 py-8 text-center" colSpan={(user?.isAdmin ? 20 : 19)}>Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={(user?.isAdmin ? 20 : 19)}>
                    <div className="mx-4 my-2 px-4 py-3 bg-[#ecf8f1] border border-[#14532d] rounded text-center text-[#14532d]">
                      <div className="text-base font-medium">No records found</div>
                      <div className="text-sm mt-1 text-green-800/80">Try adjusting your search or add a new customer.</div>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((c, idx) => (
                  <tr key={c.id ?? idx} className="hover:bg-gray-50 h-20 align-middle">
                    {user?.isAdmin && (
                      <td className="px-2 py-2 border border-[#e6efe6] text-center action-col">
                        <div className="inline-flex items-center gap-2 justify-center">
                          <button 
                            onClick={() => handleEdit(c)}
                            className="p-2 bg-[#0b5b3b] text-white rounded-full"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M16.5 3.964a2.121 2.121 0 013 3L7 19.5H4v-3L16.5 3.964z" /></svg>
                          </button>
                          <button 
                            title="Delete" 
                            onClick={() => handleDelete(c.id)}
                            className="p-2 bg-red-600 text-white rounded-full"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      </td>
                    )}

                    <td className="px-2 py-2 border border-[#e6efe6] text-center w-[6px] Srn">{(page - 1) * pageSizeDefault + idx + 1}</td>

                    <td className="px-4 py-2 border border-[#e6efe6] truncate min-w-[200px] max-w-[220px]" title={(c.firmName || c.name) || ''}>{c.firmName || c.name}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] truncate min-w-[160px] max-w-[200px]" title={c.name || ''}>{c.name}</td>

                    <td className="px-4 py-2 border border-[#e6efe6] min-w-[160px] max-w-[180px]">
                      <div className="text-xs text-gray-500 truncate" title={c.mobileNumberRef1 || ''}>{c.mobileNumberRef1 || ''}</div>
                      <div className="truncate" title={c.mobileNumber1 || ''}>{c.mobileNumber1 || '-'}</div>
                    </td>

                    <td className="px-4 py-2 border border-[#e6efe6] min-w-[160px] max-w-[180px]">
                      <div className="text-xs text-gray-500 truncate" title={c.mobileNumberRef2 || ''}>{c.mobileNumberRef2 || ''}</div>
                      <div className="truncate" title={c.mobileNumber2 || ''}>{c.mobileNumber2 || '-'}</div>
                    </td>

                    <td className="px-4 py-2 border border-[#e6efe6] min-w-[160px] max-w-[180px]">
                      <div className="text-xs text-gray-500 truncate" title={c.mobileNumberRef3 || ''}>{c.mobileNumberRef3 || ''}</div>
                      <div className="truncate" title={c.mobileNumber3 || ''}>{c.mobileNumber3 || '-'}</div>
                    </td>

                    <td className="px-4 py-2 border border-[#e6efe6] min-w-[160px] max-w-[180px]">
                      <div className="text-xs text-gray-500 truncate" title={c.mobileNumberRef4 || ''}>{c.mobileNumberRef4 || ''}</div>
                      <div className="truncate" title={c.mobileNumber4 || ''}>{c.mobileNumber4 || '-'}</div>
                    </td>

                    <td className="px-4 py-2 border border-[#e6efe6] min-w-[160px] max-w-[180px]">
                      <div className="text-xs text-gray-500 truncate" title={c.mobileNumberRef5 || ''}>{c.mobileNumberRef5 || ''}</div>
                      <div className="truncate" title={c.mobileNumber5 || ''}>{c.mobileNumber5 || '-'}</div>
                    </td>

                    <td className="px-4 py-2 border border-[#e6efe6] truncate min-w-[140px] max-w-[200px]" title={c.aadharCardNumber || ''}>{c.aadharCardNumber || '-'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] truncate min-w-[260px] max-w-[340px]" title={c.permanentAddress || ''}>{c.permanentAddress || '-'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] truncate min-w-[160px] max-w-[180px]" title={c.city || ''}>{c.city || '-'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] truncate min-w-[160px] max-w-[180px]" title={c.state || ''}>{c.state || '-'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] truncate min-w-[140px] max-w-[180px]" title={c.panCardNumber || ''}>{c.panCardNumber || '-'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] text-center">
                      {c.panCardPhoto && (
                        <ImagePreview src={c.panCardPhoto} alt={`pan-${c.name}`} className="w-24 h-16 object-cover rounded" />
                      )}
                    </td>
                    <td className="px-4 py-2 border border-[#e6efe6] truncate min-w-[140px] max-w-[180px]" title={c.gstNumber || ''}>{c.gstNumber || '-'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] truncate min-w-[160px] max-w-[200px]" title={c.udhyogAadharNumber || ''}>{c.udhyogAadharNumber || '-'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] truncate max-w-[180px]" title={c.udhyogAadharCategory || ''}>{c.udhyogAadharCategory || '-'}</td>

                    <td className="px-4 py-2 border border-[#e6efe6] text-center">
                      {c.gstPhoto && (
                        <ImagePreview src={c.gstPhoto} alt={`gst-${c.name}`} className="w-24 h-16 object-cover rounded" />
                      )}
                    </td>

                    <td className="px-4 py-2 border border-[#e6efe6] text-center">
                      {c.udhyogAadharPhoto && (
                        <ImagePreview src={c.udhyogAadharPhoto} alt={`udhyog-${c.name}`} className="w-24 h-16 object-cover rounded" />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-end gap-3 mt-4 px-4">
          <button className="btn-secondary" disabled={!canPrev} onClick={()=> setPage(p=> Math.max(1, p-1))}>Previous</button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button className="btn-primary" disabled={!canNext} onClick={()=> setPage(p=> p+1)}>Next</button>
        </div>

        <CustomerFormModal
          isOpen={showModal}
          onClose={handleCloseModal}
          onSave={handleSaveCustomer}
          editData={selectedCustomer}
        />
      </div>
    </div>
  );
};

export default CustomerDetailsPage;

